import {adminBatchApi, AdminBatchJobDetailParams, AdminBatchJobFilterParams} from '../api/adminBatchApi.ts';
import {useBaseMutation, useBaseQuery} from './useBaseQuery.ts';
import {PaginationData} from '../models/paginationData.ts';
import {AdminBatchJob, AdminBatchJobDetail, AdminBatchSubmission, ExecutionMode} from '../types/adminBatch.ts';

export const adminBatchKeys = {
    all: ['admin-batch'] as const,
    jobs: () => [...adminBatchKeys.all, 'jobs'] as const,
    jobList: (params: AdminBatchJobFilterParams) => [...adminBatchKeys.jobs(), params] as const,
    jobDetail: (jobId: number, params?: AdminBatchJobDetailParams) => [...adminBatchKeys.jobs(), 'detail', jobId, params] as const,
};

export const useAdminBatchJobs = (params: AdminBatchJobFilterParams) => {
    return useBaseQuery<PaginationData<AdminBatchJob>>(
        adminBatchKeys.jobList(params),
        () => adminBatchApi.getJobs(params),
        {staleTime: 5000},
    );
};

export const useAdminBatchJobDetail = (
    jobId?: number,
    paramsOrEnabled?: AdminBatchJobDetailParams | boolean,
    enabled: boolean = true,
) => {
    const params = typeof paramsOrEnabled === 'object' && paramsOrEnabled !== null
        ? paramsOrEnabled
        : undefined;
    const resolvedEnabled = typeof paramsOrEnabled === 'boolean' ? paramsOrEnabled : enabled;
    return useBaseQuery<AdminBatchJobDetail>(
        adminBatchKeys.jobDetail(jobId ?? 0, params),
        () => adminBatchApi.getJobDetail(jobId ?? 0, params),
        {staleTime: 5000, enabled: !!jobId && resolvedEnabled},
    );
};

export const useCancelAdminBatchJob = () => {
    return useBaseMutation<number, AdminBatchJob>((jobId) => adminBatchApi.cancelJob(jobId));
};

export const useCancelAdminBatchSubmission = () => {
    return useBaseMutation<{ jobId: number; submissionId: number }, AdminBatchSubmission>(
        ({jobId, submissionId}) => adminBatchApi.cancelSubmission(jobId, submissionId),
    );
};

export const useRetryAdminBatchSubmissionNextProvider = () => {
    return useBaseMutation<{ jobId: number; submissionId: number }, AdminBatchSubmission>(
        ({jobId, submissionId}) => adminBatchApi.retrySubmissionNextProvider(jobId, submissionId),
    );
};

export const useRecheckAdminBatchSubmissionNow = () => {
    return useBaseMutation<
        { jobId: number; submissionId: number },
        {
            jobId: number;
            submissionId: number;
            previousStatus: 'SUBMITTED' | 'COMPLETED' | 'FAILED';
            currentStatus: 'SUBMITTED' | 'COMPLETED' | 'FAILED';
            action: string;
            externalStatus?: 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'EXPIRED';
        }
    >(
        ({jobId, submissionId}) => adminBatchApi.recheckSubmissionNow(jobId, submissionId),
    );
};

export const useReprocessAllCached = () => {
    return useBaseMutation<
        { jobId: number },
        {
            jobId: number;
            totalSubmissions: number;
            processed: number;
            newGenerated: number;
            skipped: number;
            failed: number;
            noCache: number;
        }
    >(
        ({jobId}) => adminBatchApi.reprocessAllCached(jobId),
    );
};

export const useReprocessCachedSubmission = () => {
    return useBaseMutation<
        { jobId: number; submissionId: number },
        {
            submissionId: number;
            previousStatus: string;
            currentStatus: string;
            generated: number;
            skipped: number;
            failed: number;
            errorMessage?: string;
        }
    >(
        ({jobId, submissionId}) => adminBatchApi.reprocessCachedSubmission(jobId, submissionId),
    );
};

export const useResolveStaleAdminBatchSubmissions = () => {
    return useBaseMutation<
        { jobId: number; staleMinutes: number; action: 'CANCEL' | 'RETRY_NEXT_PROVIDER' },
        {
            jobId: number;
            action: 'CANCEL' | 'RETRY_NEXT_PROVIDER';
            staleMinutes: number;
            scanned: number;
            matched: number;
            canceled: number;
            retried: number;
            errors: number;
            pendingRemaining: number;
        }
    >(
        ({jobId, staleMinutes, action}) => adminBatchApi.resolveStaleSubmissions(jobId, staleMinutes, action),
    );
};

export const useRetryFailedAdminBatchSubmissions = () => {
    return useBaseMutation<
        { jobId: number; executionMode: ExecutionMode },
        { jobId: number; executionMode: ExecutionMode; created: number }
    >(
        ({jobId, executionMode}) => adminBatchApi.retryFailedSubmissions(jobId, executionMode),
    );
};

export const useRetryAdminBatchSubmission = () => {
    return useBaseMutation<
        { jobId: number; submissionId: number; executionMode: ExecutionMode; provider?: string },
        AdminBatchSubmission
    >(
        ({jobId, submissionId, executionMode, provider}) =>
            adminBatchApi.retrySubmission(jobId, submissionId, executionMode, provider),
    );
};
